import React, { useState } from 'react';
import { 
  ChartBarIcon, 
  HandRaisedIcon, 
  MusicalNoteIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import InfoTooltip from './InfoTooltip';

const PerformanceSummary = ({ analysisResults }) => {
  const [fingerFilterDirection, setFingerFilterDirection] = useState('all'); // 'up', 'down', 'all'
  
  if (!analysisResults) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-gray-500 text-center">No analysis results available</p>
      </div>
    );
  }

  // Parse FullSessionMetrics structure
  const {
    exercise_name,
    analysis,
    left_hand_stats,
    right_hand_stats,
    overall_stats,
    balance,
    synchronization,
    metronome_enabled,
    beat_emphasis,
    session_id,
    play_time_elapsed,
    missed_notes,
    erroneous_notes,
    correct_notes,
    precision,
    recall,
    f1_score,
    avg_duration
  } = analysisResults;

  // Extract values from nested analysis structure
  const calculated_tempo = analysis?.overall_tempo_bpm || 0;
  const octaves_left = analysis?.left_hand_analysis?.range_info?.octaves || 0;
  const octaves_right = analysis?.right_hand_analysis?.range_info?.octaves || 0;
  const notes_played = analysisResults.midi_roll?.length || 0;
  const bpm = calculated_tempo;
  const articulation_type = analysis?.articulation_type || 'legato';
  
  // Calculate articulation difference between hands (duration)
  const left_duration_avg = left_hand_stats?.relative_duration?.avg || 0;
  const right_duration_avg = right_hand_stats?.relative_duration?.avg || 0;
  const duration_diff = right_duration_avg - left_duration_avg;
  
  // Extract per-hand metrics
  const lh_missed = left_hand_stats?.missed_notes || 0;
  const rh_missed = right_hand_stats?.missed_notes || 0;
  const lh_wrong = left_hand_stats?.erroneous_notes || 0;
  const rh_wrong = right_hand_stats?.erroneous_notes || 0;
  const lh_lateness = left_hand_stats?.lateness?.avg || 0;
  const rh_lateness = right_hand_stats?.lateness?.avg || 0;
  const lh_beat_emphasis = left_hand_stats?.beat_emphasis || 0;
  const rh_beat_emphasis = right_hand_stats?.beat_emphasis || 0;
  
  // Extract articulation accuracy
  const lh_articulation_accuracy = analysis?.left_hand_analysis?.percent_correct_articulation || 0;
  const rh_articulation_accuracy = analysis?.right_hand_analysis?.percent_correct_articulation || 0;
  const overall_articulation_accuracy = ((lh_articulation_accuracy + rh_articulation_accuracy) / 2) || 0;
  
  // Extract finger statistics from HandStatistics
  const finger_by_finger = {};
  
  // Process left hand fingers
  if (left_hand_stats) {
    for (let i = 1; i <= 5; i++) {
      const fingerStat = left_hand_stats[`finger_${i}`];
      if (fingerStat) {
        finger_by_finger[`left_${i}`] = {
          // Combined stats (all directions)
          volume_stats_both: fingerStat.relative_volume,
          timing_stats_both: fingerStat.lateness,
          synchronization_stats_both: fingerStat.synchronization,
          duration_stats_both: fingerStat.relative_duration,
          missed_notes: fingerStat.missed_notes || 0,
          erroneous_notes: fingerStat.erroneous_notes || 0,
          
          // Up direction stats
          volume_stats_up: fingerStat.up?.relative_volume || null,
          timing_stats_up: fingerStat.up?.lateness || null,
          synchronization_stats_up: fingerStat.up?.synchronization || null,
          duration_stats_up: fingerStat.up?.relative_duration || null,
          missed_notes_up: fingerStat.up?.missed_notes || 0,
          erroneous_notes_up: fingerStat.up?.erroneous_notes || 0,
          
          // Down direction stats
          volume_stats_down: fingerStat.down?.relative_volume || null,
          timing_stats_down: fingerStat.down?.lateness || null,
          synchronization_stats_down: fingerStat.down?.synchronization || null,
          duration_stats_down: fingerStat.down?.relative_duration || null,
          missed_notes_down: fingerStat.down?.missed_notes || 0,
          erroneous_notes_down: fingerStat.down?.erroneous_notes || 0
        };
      }
    }
  }
  
  // Process right hand fingers
  if (right_hand_stats) {
    for (let i = 1; i <= 5; i++) {
      const fingerStat = right_hand_stats[`finger_${i}`];
      if (fingerStat) {
        finger_by_finger[`right_${i}`] = {
          // Combined stats (all directions)
          volume_stats_both: fingerStat.relative_volume,
          timing_stats_both: fingerStat.lateness,
          synchronization_stats_both: fingerStat.synchronization,
          duration_stats_both: fingerStat.relative_duration,
          missed_notes: fingerStat.missed_notes || 0,
          erroneous_notes: fingerStat.erroneous_notes || 0,
          
          // Up direction stats
          volume_stats_up: fingerStat.up?.relative_volume || null,
          timing_stats_up: fingerStat.up?.lateness || null,
          synchronization_stats_up: fingerStat.up?.synchronization || null,
          duration_stats_up: fingerStat.up?.relative_duration || null,
          missed_notes_up: fingerStat.up?.missed_notes || 0,
          erroneous_notes_up: fingerStat.up?.erroneous_notes || 0,
          
          // Down direction stats
          volume_stats_down: fingerStat.down?.relative_volume || null,
          timing_stats_down: fingerStat.down?.lateness || null,
          synchronization_stats_down: fingerStat.down?.synchronization || null,
          duration_stats_down: fingerStat.down?.relative_duration || null,
          missed_notes_down: fingerStat.down?.missed_notes || 0,
          erroneous_notes_down: fingerStat.down?.erroneous_notes || 0
        };
      }
    }
  }

  // Helper function to format stats
  const formatStat = (value, decimals = 1) => {
    if (value === null || value === undefined) return '-';
    return typeof value === 'number' ? value.toFixed(decimals) : value;
  };

  // Helper function to get color class for missed/erroneous notes
  const getNoteErrorColor = (value, defaultColor) => {
    const numValue = Number(value) || 0;
    return numValue === 0 ? 'text-black' : defaultColor;
  };

  // Visual bar component for balance (±10 ticks range)
  // Horizontal/flat = perfect/even at 0, Vertical = uneven at ±10
  const BalanceBar = ({ value }) => {
    const maxRange = 10;
    const absValue = Math.abs(value);
    const intensity = Math.min(absValue / maxRange, 1);
    // 0 degrees = horizontal/flat (good), rotate towards 90 degrees = vertical (bad)
    const rotation = intensity * 90;
    
    // Color interpolation: green (even) to red (uneven)
    const red = Math.round(220 * intensity + 34 * (1 - intensity));
    const green = Math.round(38 * intensity + 197 * (1 - intensity));
    const blue = Math.round(38 * intensity + 94 * (1 - intensity));
    
    return (
      <div className="flex items-center justify-center h-16 bg-gray-50 rounded">
        <div className="relative w-16 h-16">
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `rotate(${value > 0 ? rotation : -rotation}deg)`,
              transition: 'transform 0.3s ease'
            }}
          >
            <div 
              className="w-12 h-2 rounded-full"
              style={{ backgroundColor: `rgb(${red}, ${green}, ${blue})` }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Visual bar component for synchronization (100ms range)
  // Vertical = perfect, Horizontal = off
  const SyncBar = ({ value }) => {
    const maxRange = 100;
    const absValue = Math.abs(value);
    const intensity = Math.min(absValue / maxRange, 1);
    // 0 degrees = vertical (good), rotate towards 90 degrees = horizontal (bad)
    const rotation = intensity * 90;
    
    // Color: green (perfect) to red (off)
    const red = Math.round(220 * intensity + 34 * (1 - intensity));
    const green = Math.round(38 * intensity + 197 * (1 - intensity));
    const blue = Math.round(38 * intensity + 94 * (1 - intensity));
    
    return (
      <div className="flex items-center justify-center h-16 bg-gray-50 rounded">
        <div className="relative w-16 h-16">
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.3s ease'
            }}
          >
            <div 
              className="w-2 h-12 rounded-full"
              style={{ backgroundColor: `rgb(${red}, ${green}, ${blue})` }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Visual bar component for duration (10ms range)
  // Vertical = perfect, Horizontal = off
  const DurationBar = ({ value }) => {
    const maxRange = 10;
    const absValue = Math.abs(value);
    const intensity = Math.min(absValue / maxRange, 1);
    // 0 degrees = vertical (good), rotate towards 90 degrees = horizontal (bad)
    const rotation = intensity * 90;
    
    // Color: green (perfect) to red (off)
    const red = Math.round(220 * intensity + 34 * (1 - intensity));
    const green = Math.round(38 * intensity + 197 * (1 - intensity));
    const blue = Math.round(38 * intensity + 94 * (1 - intensity));
    
    return (
      <div className="flex items-center justify-center h-16 bg-gray-50 rounded">
        <div className="relative w-16 h-16">
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.3s ease'
            }}
          >
            <div 
              className="w-2 h-12 rounded-full"
              style={{ backgroundColor: `rgb(${red}, ${green}, ${blue})` }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Helper function to find extreme values in a table and determine if a value should be bold
  const getExtremeValues = (data, metric) => {
    const values = [];
    
    // Collect all non-null values for the metric from all hands/fingers
    Object.keys(data).forEach(key => {
      const stats = data[key];
      if (stats && stats[metric] && stats[metric].avg !== null && stats[metric].avg !== undefined) {
        values.push({
          key,
          value: stats[metric].avg
        });
      }
    });

    if (values.length === 0) return { max: null, min: null };

    // Find max and min values
    const sortedValues = values.sort((a, b) => b.value - a.value);
    const max = sortedValues[0];
    const min = sortedValues[sortedValues.length - 1];

    return { max, min };
  };

  // Helper function to check if a value should be bold
  const shouldBeBold = (value, extremes) => {
    if (!extremes.max || !extremes.min || value === null || value === undefined) return false;
    return value === extremes.max.value || value === extremes.min.value;
  };

  // Helper to get finger stats based on filter
  const getFingerStats = (fingerKey) => {
    const stats = finger_by_finger?.[fingerKey];
    if (!stats) return null;

    switch (fingerFilterDirection) {
      case 'up':
        return {
          volume: stats.volume_stats_up,
          timing: stats.timing_stats_up,
          synchronization: stats.synchronization_stats_up,
          duration: stats.duration_stats_up,
          missed: stats.missed_notes_up,
          erroneous: stats.erroneous_notes_up
        };
      case 'down':
        return {
          volume: stats.volume_stats_down,
          timing: stats.timing_stats_down,
          synchronization: stats.synchronization_stats_down,
          duration: stats.duration_stats_down,
          missed: stats.missed_notes_down,
          erroneous: stats.erroneous_notes_down
        };
      case 'all':
      default:
        return {
          volume: stats.volume_stats_both,
          timing: stats.timing_stats_both,
          synchronization: stats.synchronization_stats_both,
          duration: stats.duration_stats_both,
          missed: stats.missed_notes,
          erroneous: stats.erroneous_notes
        };
    }
  };

  // Prepare data for extremes calculation
  const fingerData = {};
  ['left_1', 'left_2', 'left_3', 'left_4', 'left_5', 
   'right_1', 'right_2', 'right_3', 'right_4', 'right_5'].forEach(key => {
    fingerData[key] = getFingerStats(key);
  });

  // Calculate extremes for finger stats
  const volumeExtremes = getExtremeValues(fingerData, 'volume');
  const timingExtremes = getExtremeValues(fingerData, 'timing');
  const synchronizationExtremes = getExtremeValues(fingerData, 'synchronization');
  const durationExtremes = getExtremeValues(fingerData, 'duration');

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
      {/* Header */}
      <div className="border-b pb-3">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Performance Analysis</h2>
        <p className="text-lg text-gray-600">
          {exercise_name ? exercise_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown Exercise'}
        </p>
      </div>

      {/* Performance Info - Evenly Spaced Across Screen */}
      <div className="bg-gray-50 rounded-lg p-3">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Performance Info</h3>
        <div className="flex justify-around items-center text-sm">
          <div className="text-center">
            <div className="text-gray-600 text-xs">Calculated Tempo</div>
            <div className="font-semibold">{formatStat(calculated_tempo, 0)} BPM</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600 text-xs">LH Range</div>
            <div className="font-semibold">{octaves_left} oct</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600 text-xs">RH Range</div>
            <div className="font-semibold">{octaves_right} oct</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600 text-xs">Articulation</div>
            <div className="font-semibold capitalize">{articulation_type}</div>
          </div>
        </div>
      </div>

      {/* Combined Stats Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Stats</h3>
        
        {/* Accuracy - More Compact */}
        <div className="mb-4">
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div className="text-center">
              <CheckCircleIcon className="h-6 w-6 text-green-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-600">{correct_notes || 0}</p>
              <p className="text-xs text-gray-600 mt-1">Correct</p>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <span className="text-gray-600 text-xs block">F1</span>
                <span className="font-semibold text-sm">{formatStat(f1_score * 100, 1)}%</span>
              </div>
            </div>
            <div className="text-center">
              <div className="mt-7">
                <p className={`text-2xl font-bold ${(missed_notes || 0) === 0 ? 'text-black' : 'text-red-600'}`}>
                  {missed_notes || 0}
                </p>
              </div>
              <p className="text-xs text-gray-600 mt-1">Missed</p>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <span className="text-gray-600 text-xs block">Recall</span>
                <span className="font-semibold text-sm">{formatStat(recall * 100, 1)}%</span>
              </div>
            </div>
            <div className="text-center">
              <div className="mt-7">
                <p className={`text-2xl font-bold ${(erroneous_notes || 0) === 0 ? 'text-black' : 'text-red-600'}`}>
                  {erroneous_notes || 0}
                </p>
              </div>
              <p className="text-xs text-gray-600 mt-1">Wrong</p>
              <div className="mt-2 pt-2 border-t border-gray-200">
                <span className="text-gray-600 text-xs block">Precision</span>
                <span className="font-semibold text-sm">{formatStat(precision * 100, 1)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Hand Statistics - Visual Bars Get Half Width */}
        <div className="pt-3 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Visual Bars - Half Width */}
            <div className="flex items-center justify-around gap-2">
              <div className="text-center flex-1">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  Balance
                  <InfoTooltip 
                    content="How much louder the notes you played in this group are relative to average volume"
                    position="top"
                  />
                </div>
                <BalanceBar value={balance} />
                <div className="text-xs font-semibold mt-1">
                  {balance > 0 ? `R+${formatStat(balance, 1)}` : balance < 0 ? `L+${formatStat(Math.abs(balance), 1)}` : 'Even'}
                </div>
              </div>
              
              <div className="text-center flex-1">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  Synchronization
                  <InfoTooltip 
                    content="How late (in ms) the notes you played are relative to the other hand. Will be 0 if only one hand played."
                    position="top"
                  />
                </div>
                <SyncBar value={synchronization} />
                <div className="text-xs font-semibold mt-1">
                  {synchronization > 0 ? `R+${formatStat(synchronization, 1)}` : 
                   synchronization < 0 ? `L+${formatStat(Math.abs(synchronization), 1)}` : 
                   'Perfect'}
                </div>
              </div>
              
              <div className="text-center flex-1">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  Duration
                  <InfoTooltip 
                    content="How much longer (in ms) the notes you played in this group are relative to the average note played"
                    position="top"
                  />
                </div>
                <DurationBar value={duration_diff} />
                <div className="text-xs font-semibold mt-1">
                  {duration_diff > 0 ? `R+${formatStat(duration_diff, 1)}` : 
                   duration_diff < 0 ? `L+${formatStat(Math.abs(duration_diff), 1)}` : 
                   'Even'}%
                </div>
              </div>
            </div>

            {/* Text Stats - Stats as Rows, Hands as Columns */}
            <div className="text-sm bg-white rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="bg-gray-50 px-4 py-2 text-left text-xs font-medium text-gray-600"></th>
                    <th className="bg-gray-50 px-4 py-2 text-center text-xs font-medium text-gray-600">LH</th>
                    <th className="bg-gray-50 px-4 py-2 text-center text-xs font-medium text-gray-600">RH</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="hover:bg-gray-50">
                    <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-700">
                      Beat Emphasis
                      <InfoTooltip 
                        content="How much louder (as a %) the notes you played on the beat are vs average"
                        position="right"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">{formatStat(lh_beat_emphasis, 1)}%</td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">{formatStat(rh_beat_emphasis, 1)}%</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-700">
                      Lateness
                      <InfoTooltip 
                        content="How late (in ms) the notes you played are relative to where we expected them to be"
                        position="right"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">{formatStat(lh_lateness, 1)} ms</td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">{formatStat(rh_lateness, 1)} ms</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-700">
                      Articulation
                      <InfoTooltip 
                        content="The % of notes you played with the correct articulation"
                        position="right"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">{formatStat(lh_articulation_accuracy, 1)}%</td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">{formatStat(rh_articulation_accuracy, 1)}%</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-700">
                      Missed / Wrong
                      <InfoTooltip 
                        content="Missed: notes we expected but didn't see. Wrong: notes you played that we didn't expect"
                        position="right top"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">
                      <span className={lh_missed > 0 ? 'text-red-600' : ''}>{lh_missed}</span>
                      {' / '}
                      <span className={lh_wrong > 0 ? 'text-orange-600' : ''}>{lh_wrong}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center font-medium text-gray-900">
                      <span className={rh_missed > 0 ? 'text-red-600' : ''}>{rh_missed}</span>
                      {' / '}
                      <span className={rh_wrong > 0 ? 'text-orange-600' : ''}>{rh_wrong}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Filter buttons for finger analysis */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Finger-by-Finger Analysis</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setFingerFilterDirection('all')}
            className={`px-3 py-1 text-xs rounded ${
              fingerFilterDirection === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFingerFilterDirection('up')}
            className={`px-3 py-1 text-xs rounded ${
              fingerFilterDirection === 'up' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Up
          </button>
          <button
            onClick={() => setFingerFilterDirection('down')}
            className={`px-3 py-1 text-xs rounded ${
              fingerFilterDirection === 'down' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Down
          </button>
        </div>
      </div>

      {/* Finger-by-Finger Statistics */}
      {Object.keys(finger_by_finger).length > 0 && (
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Finger</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Volume
                    <InfoTooltip 
                      content="How much louder the notes you played in this group are relative to average volume"
                      position="bottom"
                    />
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lateness
                    <InfoTooltip 
                      content="How late (in ms) the notes you played are relative to where we expected them to be"
                      position="bottom"
                    />
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Synchronization
                    <InfoTooltip 
                      content="How late (in ms) the notes you played are relative to the other hand. Will be 0 if only one hand played"
                      position="bottom"
                    />
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                    <InfoTooltip 
                      content="How much longer (in ms) the notes you played in this group are relative to the average note played"
                      position="bottom"
                    />
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Missed
                    <InfoTooltip 
                      content="How many notes we expected to see but didn't"
                      position="bottom"
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Left Hand Fingers */}
                {[1, 2, 3, 4, 5].map(finger => {
                  const stats = getFingerStats(`left_${finger}`);
                  if (!stats) return null;
                  
                  return (
                    <tr key={`left_${finger}`}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">L{finger}</td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.volume?.avg, volumeExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(stats.volume?.avg)}
                        {stats.volume?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.volume.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.timing?.avg, timingExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(stats.timing?.avg)}
                        {stats.timing?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.timing.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.synchronization?.avg, synchronizationExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(Math.abs(stats.synchronization?.avg || 0))}
                        {stats.synchronization?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.synchronization.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.duration?.avg, durationExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(stats.duration?.avg)}
                        {stats.duration?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.duration.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${getNoteErrorColor(stats.missed, 'text-red-600')}`}>
                        {stats.missed || 0}
                      </td>
                    </tr>
                  );
                })}
                
                {/* Right Hand Fingers */}
                {[1, 2, 3, 4, 5].map(finger => {
                  const stats = getFingerStats(`right_${finger}`);
                  if (!stats) return null;
                  
                  return (
                    <tr key={`right_${finger}`}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">R{finger}</td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.volume?.avg, volumeExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(stats.volume?.avg)}
                        {stats.volume?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.volume.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.timing?.avg, timingExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(stats.timing?.avg)}
                        {stats.timing?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.timing.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.synchronization?.avg, synchronizationExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(Math.abs(stats.synchronization?.avg || 0))}
                        {stats.synchronization?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.synchronization.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${
                        shouldBeBold(stats.duration?.avg, durationExtremes) ? 'font-bold' : ''
                      }`}>
                        {formatStat(stats.duration?.avg)}
                        {stats.duration?.std_dev ? 
                          <span className="text-xs text-gray-400 ml-1">±{formatStat(stats.duration.std_dev)}</span> : null}
                      </td>
                      <td className={`px-4 py-2 text-sm text-center ${getNoteErrorColor(stats.missed, 'text-red-600')}`}>
                        {stats.missed || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceSummary;